import type { ProfileGroup, RiskModelInstance, DecisionResult } from '@/types/risk-profile';
import { EXPOSED_DECISION_RISK_LEVEL_SLUGS } from '@/types/risk-profile';

// Presentation for the auto-decision column shared by the policy and template tables.
// Editable presentation (Tailwind class variants) lives in DecisionLogicPanel.vue; these
// hex tones are intentionally bright to read on the dark PrimeVue tooltip background.
const RISK_LABELS_PT: Record<string, string> = { low: 'Baixo', medium: 'Médio', high: 'Alto', very_high: 'Muito Alto' };
const RESULT_META: Record<DecisionResult, { icon: string; color: string }> = {
    approve: { icon: 'check_circle', color: '#4ade80' },
    decline: { icon: 'cancel', color: '#f87171' },
    manual: { icon: 'pan_tool', color: '#cbd5e1' },
};

// The store accessors both the policy and template stores expose for resolving instances.
interface InstanceAccessors {
    getCurrentInstance: (group: ProfileGroup) => RiskModelInstance | undefined;
    getLatestRelease: (group: ProfileGroup) => RiskModelInstance | null;
    getDraft: (group: ProfileGroup) => RiskModelInstance | undefined;
}

export function usePolicyDecisionDisplay(store: InstanceAccessors) {
    // The instance whose decision logic represents the group: production first, then latest release, then draft.
    const getDecisionInstance = (group: ProfileGroup) =>
        store.getCurrentInstance(group) || store.getLatestRelease(group) || store.getDraft(group);

    const hasPolicyDecision = (group: ProfileGroup): boolean => !!getDecisionInstance(group)?.decision_logic;

    // Returns a PrimeVue tooltip options object. When configured, renders the risk levels as a list
    // (escape:false so the HTML is rendered), each prefixed with the decision's colored icon.
    const policyDecisionTooltip = (group: ProfileGroup): { value: string; escape: boolean } => {
        const inst = getDecisionInstance(group);
        const logic = inst?.decision_logic;
        if (!logic) return { value: 'Sem decisão automática configurada', escape: true };

        const bySlug = new Map(logic.rules.map(r => [r.risk_level_slug, r.result]));
        const rows = EXPOSED_DECISION_RISK_LEVEL_SLUGS
            .filter(slug => bySlug.has(slug))
            .map(slug => {
                const meta = RESULT_META[bySlug.get(slug) ?? 'manual'];
                return `<div style="display:flex;align-items:center;gap:8px;line-height:26px;white-space:nowrap;">`
                    + `<span class="material-icons" style="font-size:18px;color:${meta.color};">${meta.icon}</span>`
                    + `<span style="color:#f8fafc;">${RISK_LABELS_PT[slug] ?? slug}</span>`
                    + `</div>`;
            })
            .join('');
        const version = inst?.decision_version != null ? ` (v${inst.decision_version})` : '';
        const header = `<div style="font-weight:600;margin-bottom:6px;color:#f8fafc;">Decisão automática${version}</div>`;
        return { value: `<div>${header}${rows}</div>`, escape: false };
    };

    return { getDecisionInstance, hasPolicyDecision, policyDecisionTooltip };
}
